// /bestellingen – barcodes scannen, gegroepeerd per categorie/subcategorie, mailen (foto's 4 en 5)
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, foutTekst } from "../api";
import { Kop } from "../components/Onderdelen";

function piep(ok = true) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.value = ok ? 1200 : 300; o.connect(g); g.connect(ctx.destination);
    g.gain.value = 0.15; o.start(); o.stop(ctx.currentTime + (ok ? 0.08 : 0.3));
  } catch { /* geen geluid mogelijk */ }
}

export default function Bestellingen({ onAfmelden }) {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);       // {barcode,naam,categorie,subcategorie,aantal}
  const [onbekend, setOnbekend] = useState([]); // {barcode,aantal}
  const [code, setCode] = useState("");
  const [melding, setMelding] = useState(null); // {ok, tekst}
  const [flits, setFlits] = useState(null);
  const [bevestig, setBevestig] = useState(false);
  const [bezig, setBezig] = useState(false);
  const invoer = useRef(null);
  const geladen = useRef(false);
  const cache = useRef({});

  useEffect(() => {
    api.get("/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/bestellingen/concept").then((r) => {
      setItems(r.data.items || []); setOnbekend(r.data.onbekend || []);
    }).finally(() => { geladen.current = true; });
  }, []);

  // concept bewaren (na 600 ms stilte)
  useEffect(() => {
    if (!geladen.current) return;
    const t = setTimeout(() => api.put("/bestellingen/concept", { items, onbekend }).catch(() => {}), 600);
    return () => clearTimeout(t);
  }, [items, onbekend]);

  // focus altijd in het scanveld
  useEffect(() => {
    const f = () => !bevestig && invoer.current?.focus();
    f(); const t = setInterval(f, 800);
    return () => clearInterval(t);
  }, [bevestig]);

  const toon = (ok, tekst) => { setMelding({ ok, tekst }); setTimeout(() => setMelding(null), 2500); };

  const verwerk = useCallback(async (barcode) => {
    barcode = barcode.trim();
    if (!barcode) return;
    let p = cache.current[barcode];
    if (!p) {
      try { p = (await api.get(`/bestellingen/scan/${encodeURIComponent(barcode)}`)).data; cache.current[barcode] = p; }
      catch (e) {
        piep(false);
        setOnbekend((o) => {
          const i = o.findIndex((x) => x.barcode === barcode);
          if (i < 0) return [...o, { barcode, aantal: 1 }];
          const k = [...o]; k[i] = { ...k[i], aantal: k[i].aantal + 1 }; return k;
        });
        toon(false, e?.response?.status === 404 ? `Onbekende barcode: ${barcode}` : foutTekst(e));
        return;
      }
    }
    piep(true);
    setItems((lijst) => {
      const i = lijst.findIndex((x) => x.barcode === barcode);
      if (i < 0) return [...lijst, { ...p, aantal: 1 }];
      const k = [...lijst]; k[i] = { ...k[i], aantal: k[i].aantal + 1 }; return k;
    });
    setFlits(barcode); setTimeout(() => setFlits((f) => (f === barcode ? null : f)), 1000);
  }, []);

  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); const c = code; setCode(""); verwerk(c); }
  };

  const wijzig = (barcode, delta) =>
    setItems((l) => l.map((x) => (x.barcode === barcode ? { ...x, aantal: x.aantal + delta } : x)).filter((x) => x.aantal > 0));

  const groepen = useMemo(() => {
    const b = {};
    items.forEach((it) => { ((b[it.categorie] ||= {})[it.subcategorie] ||= []).push(it); });
    const s = (a, c) => a.localeCompare(c, "nl", { sensitivity: "base" });
    return Object.keys(b).sort(s).map((cat) => ({
      cat, subs: Object.keys(b[cat]).sort(s).map((sub) => ({ sub, art: [...b[cat][sub]].sort((x, y) => s(x.naam, y.naam)) })),
    }));
  }, [items]);

  const stuks = items.reduce((t, x) => t + x.aantal, 0);

  const verzend = async () => {
    setBezig(true);
    try {
      const r = await api.post("/bestellingen/verzend", { items, onbekend });
      setItems([]); setOnbekend([]); setBevestig(false);
      toon(true, `✓ Bestelling verzonden (${r.data.artikelen} artikelen · ${r.data.stuks} stuks)`);
    } catch (e) { setBevestig(false); toon(false, foutTekst(e)); }
    finally { setBezig(false); }
  };

  return (
    <div className="min-h-screen bg-[#f1f4f9] flex flex-col">
      <Kop titel="Bestellingen" sub={me?.vestiging?.naam || ""} onAfmelden={onAfmelden} />
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-center gap-3 bg-[#1e2a44] text-white rounded-2xl px-4 py-3.5">
          <span className="text-xl">📷</span>
          <input ref={invoer} value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={onKey}
            placeholder="Scan een barcode…" autoFocus data-testid="scan-invoer"
            className="flex-1 bg-[#2b3a5c] border-2 border-blue-500 rounded-xl px-3 py-3 text-lg outline-none placeholder:text-slate-400" />
          <span className="text-xs text-blue-200 max-w-[240px] hidden md:block">Scan na elkaar — hetzelfde artikel opnieuw scannen telt +1</span>
        </div>
        {melding && (
          <div className={`p-3 rounded-xl text-sm font-semibold ${melding.ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{melding.tekst}</div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-4 flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-500 border-b">
              <th className="text-left py-2">Artikel (zoals in systeem)</th><th className="text-left">Barcode</th><th className="text-right">Aantal</th>
            </tr></thead>
            <tbody>
              {groepen.length === 0 && <tr><td colSpan={3} className="py-10 text-center text-slate-400">Nog niets gescand.</td></tr>}
              {groepen.map((g) => [
                <tr key={g.cat} className="bg-slate-50"><td colSpan={3} className="py-2 px-2 font-bold text-blue-600 text-xs uppercase">{g.cat}</td></tr>,
                ...g.subs.flatMap((s) => [
                  <tr key={g.cat + s.sub}><td colSpan={3} className="py-1.5 pl-4 text-xs font-semibold text-slate-500">{s.sub}</td></tr>,
                  ...s.art.map((a) => (
                    <tr key={a.barcode} className={`border-b border-slate-100 transition-colors ${flits === a.barcode ? "bg-green-100" : ""}`}>
                      <td className="py-2 px-2">{a.naam}</td>
                      <td className="text-slate-600">{a.barcode}</td>
                      <td className="text-right">
                        <span className="inline-flex items-center gap-2">
                          <button onClick={() => wijzig(a.barcode, -1)} className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 font-bold">−</button>
                          <b className="w-6 text-center">{a.aantal}</b>
                          <button onClick={() => wijzig(a.barcode, +1)} className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 font-bold">+</button>
                        </span>
                      </td>
                    </tr>
                  )),
                ]),
              ])}
            </tbody>
          </table>
          {onbekend.length > 0 && (
            <div className="mt-4 p-3 rounded-xl bg-red-50 text-sm text-red-700">
              <b>⚠ Onbekende barcodes:</b> {onbekend.map((o) => `${o.barcode} (${o.aantal}x)`).join(", ")}
              <button className="ml-3 underline" onClick={() => setOnbekend([])}>wissen</button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-600">{items.length} artikelen · {stuks} stuks · wordt verzonden naar <b>gurjashansingh1@gmail.com</b></span>
          <button className="ml-auto bg-blue-50 text-blue-600 font-semibold rounded-xl px-4 py-2.5 text-sm"
            onClick={() => window.confirm("Lijst leegmaken?") && (setItems([]), setOnbekend([]))}>🗑 Lijst leegmaken</button>
          <button disabled={!items.length && !onbekend.length} onClick={() => setBevestig(true)}
            className="bg-green-600 disabled:opacity-40 text-white font-bold rounded-xl px-7 py-3.5">✓ Bevestigen &amp; mailen</button>
        </div>
      </div>

      {bevestig && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center">
            <h3 className="text-lg font-bold text-slate-800">Bestelling versturen?</h3>
            <p className="text-sm text-slate-600 my-3">{items.length} artikelen · {stuks} stuks<br />naar gurjashansingh1@gmail.com</p>
            <div className="flex gap-2 justify-center">
              <button className="px-5 py-2.5 rounded-xl bg-slate-100 font-semibold" onClick={() => setBevestig(false)}>Annuleren</button>
              <button disabled={bezig} className="px-5 py-2.5 rounded-xl bg-green-600 text-white font-semibold" onClick={verzend}>
                {bezig ? "Bezig…" : "Ja, versturen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
